import React, { useState, useEffect, useCallback } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { Plus, Pencil, Trash2, X, ChevronDown, ChevronUp, Search, Tag } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';

// ── Shared helpers ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-primary-500' : 'bg-slate-200'}`}>
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
    </button>
  );
}

// ── Qualifications Tab ─────────────────────────────────────────────────────────
function QualificationsTab({ categories, qualifications, supportNeeds, onRefresh }) {
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null); // {type:'qual'|'cat', item?}
  const [saving, setSaving] = useState(false);

  const [qualForm, setQualForm] = useState({
    name: '', category_id: '', description: '',
    requires_expiry: false, requires_document: false, support_need_ids: [],
  });
  const [catForm, setCatForm] = useState({ name: '' });

  const openNewQual = (catId = '') => {
    setQualForm({ name: '', category_id: catId, description: '', requires_expiry: false, requires_document: false, support_need_ids: [] });
    setModal({ type: 'qual' });
  };
  const openEditQual = q => {
    setQualForm({
      name: q.name, category_id: q.category_id || '',
      description: q.description || '',
      requires_expiry: q.requires_expiry,
      requires_document: q.requires_document,
      support_need_ids: Array.isArray(q.support_need_ids) ? q.support_need_ids : [],
    });
    setModal({ type: 'qual', item: q });
  };
  const openNewCat = () => { setCatForm({ name: '' }); setModal({ type: 'cat' }); };

  const saveQual = async () => {
    if (!qualForm.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (modal.item) {
        await api.put(`/smart-match/qualifications/${modal.item.id}`, qualForm);
        toast.success('Qualification updated');
      } else {
        await api.post('/smart-match/qualifications', qualForm);
        toast.success('Qualification added');
      }
      setModal(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteQual = async q => {
    if (!await confirm(`Delete "${q.name}"?`, { title: 'Delete Qualification', danger: true })) return;
    try {
      await api.delete(`/smart-match/qualifications/${q.id}`);
      toast.success('Deleted');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post('/smart-match/categories', catForm);
      toast.success('Category added');
      setModal(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteCat = async cat => {
    if (!await confirm(`Delete category "${cat.name}"? All qualifications in this category will be unlinked.`, { title: 'Delete Category', danger: true })) return;
    try {
      await api.delete(`/smart-match/categories/${cat.id}`);
      toast.success('Deleted');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const toggleSN = id => {
    setQualForm(f => ({
      ...f,
      support_need_ids: f.support_need_ids.includes(id)
        ? f.support_need_ids.filter(x => x !== id)
        : [...f.support_need_ids, id],
    }));
  };

  const filteredQuals = (catId) => {
    const base = qualifications.filter(q => q.category_id === catId);
    if (!search.trim()) return base;
    return base.filter(q => q.name.toLowerCase().includes(search.toLowerCase()));
  };

  const toggle = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-field pl-8 text-sm" placeholder="Search qualifications…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button onClick={openNewCat} className="btn-secondary btn-sm flex items-center gap-1.5">
            <Plus size={14} /> New Category
          </button>
          <button onClick={() => openNewQual()} className="btn-primary btn-sm flex items-center gap-1.5">
            <Plus size={14} /> New Qualification
          </button>
        </div>
      </div>

      {categories.map(cat => {
        const quals = filteredQuals(cat.id);
        const isOpen = expanded[cat.id] !== false; // default open
        return (
          <div key={cat.id} className="card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-100 cursor-pointer"
              onClick={() => toggle(cat.id)}>
              <div className="flex items-center gap-2">
                <Tag size={14} className="text-primary-500" />
                <span className="font-semibold text-sm text-slate-700">{cat.name}</span>
                <span className="text-xs text-slate-400 bg-slate-200 rounded-full px-2 py-0.5">{quals.length}</span>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => openNewQual(cat.id)}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-primary-50 transition-colors">
                  <Plus size={12} /> Add
                </button>
                <button onClick={() => deleteCat(cat)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                  <Trash2 size={13} />
                </button>
                <div className="text-slate-400">{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</div>
              </div>
            </div>

            {isOpen && (
              <div>
                {quals.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No qualifications in this category</p>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500">Name</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Description</th>
                        <th className="text-center px-3 py-2.5 text-xs font-medium text-slate-500">Expiry</th>
                        <th className="text-center px-3 py-2.5 text-xs font-medium text-slate-500">Document</th>
                        <th className="px-3 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {quals.map((q, i) => (
                        <tr key={q.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                          <td className="px-5 py-2.5 font-medium text-slate-700">{q.name}</td>
                          <td className="px-3 py-2.5 text-slate-400 text-xs hidden md:table-cell max-w-xs truncate">{q.description || '—'}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`inline-block w-4 h-4 rounded-full ${q.requires_expiry ? 'bg-amber-400' : 'bg-slate-200'}`} title={q.requires_expiry ? 'Required' : 'Not required'} />
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <span className={`inline-block w-4 h-4 rounded-full ${q.requires_document ? 'bg-primary-400' : 'bg-slate-200'}`} title={q.requires_document ? 'Required' : 'Not required'} />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => openEditQual(q)}
                                className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors">
                                <Pencil size={13} />
                              </button>
                              <button onClick={() => deleteQual(q)}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Uncategorised */}
      {(() => {
        const uncategorised = qualifications.filter(q => !q.category_id);
        if (!uncategorised.length) return null;
        return (
          <div className="card overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
              <span className="font-semibold text-sm text-slate-500">Uncategorised</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {uncategorised.map(q => (
                  <tr key={q.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-2.5 font-medium text-slate-700">{q.name}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEditQual(q)} className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors"><Pencil size={13} /></button>
                        <button onClick={() => deleteQual(q)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })()}

      {/* Qualification modal */}
      {modal?.type === 'qual' && (
        <Modal title={modal.item ? 'Edit Qualification' : 'New Qualification'} onClose={() => setModal(null)}>
          <Field label="Name" required>
            <input className="input-field" maxLength={200} placeholder="Qualification name"
              value={qualForm.name} onChange={e => setQualForm(f => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Category">
            <select className="input-field" value={qualForm.category_id}
              onChange={e => setQualForm(f => ({ ...f, category_id: e.target.value }))}>
              <option value="">None</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea className="input-field resize-none" rows={3} maxLength={200}
              placeholder="Brief description…"
              value={qualForm.description} onChange={e => setQualForm(f => ({ ...f, description: e.target.value }))} />
            <p className="text-right text-xs text-slate-400 mt-1">{qualForm.description.length}/200</p>
          </Field>

          {supportNeeds.length > 0 && (
            <Field label="Assign to Support Needs">
              <div className="flex flex-wrap gap-2 mt-1">
                {supportNeeds.map(sn => (
                  <button key={sn.id} type="button" onClick={() => toggleSN(sn.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      qualForm.support_need_ids.includes(sn.id)
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
                    }`}>
                    {sn.name}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <Toggle checked={qualForm.requires_expiry}
                onChange={v => setQualForm(f => ({ ...f, requires_expiry: v }))} />
              <span className="text-sm text-slate-600">Requires Expiry Date</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Toggle checked={qualForm.requires_document}
                onChange={v => setQualForm(f => ({ ...f, requires_document: v }))} />
              <span className="text-sm text-slate-600">Document Required</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={saveQual} disabled={saving} className="btn-primary btn-sm">
              {saving ? 'Saving…' : modal.item ? 'Save Changes' : 'Add Qualification'}
            </button>
          </div>
        </Modal>
      )}

      {/* Category modal */}
      {modal?.type === 'cat' && (
        <Modal title="New Category" onClose={() => setModal(null)}>
          <Field label="Category Name" required>
            <input className="input-field" maxLength={100} placeholder="e.g. Compliance"
              value={catForm.name} onChange={e => setCatForm({ name: e.target.value })} />
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={saveCat} disabled={saving} className="btn-primary btn-sm">
              {saving ? 'Saving…' : 'Add Category'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Support Needs Tab ──────────────────────────────────────────────────────────
function SupportNeedsTab({ supportNeeds, onRefresh }) {
  const confirm = useConfirm();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm({ name: '', description: '' }); setModal({}); };
  const openEdit = sn => { setForm({ name: sn.name, description: sn.description || '' }); setModal({ item: sn }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (modal.item) {
        await api.put(`/smart-match/support-needs/${modal.item.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/smart-match/support-needs', form);
        toast.success('Added');
      }
      setModal(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async sn => {
    if (!await confirm(`Delete "${sn.name}"?`, { title: 'Delete Support Need', danger: true })) return;
    try {
      await api.delete(`/smart-match/support-needs/${sn.id}`);
      toast.success('Deleted');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNew} className="btn-primary btn-sm flex items-center gap-1.5">
          <Plus size={14} /> New Support Need
        </button>
      </div>

      <div className="card overflow-hidden">
        {supportNeeds.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No support needs defined yet</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-medium text-slate-500">Name</th>
                <th className="text-left px-3 py-3 text-xs font-medium text-slate-500 hidden md:table-cell">Description</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {supportNeeds.map((sn, i) => (
                <tr key={sn.id} className={`border-b border-slate-50 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                  <td className="px-5 py-3 font-medium text-slate-700">{sn.name}</td>
                  <td className="px-3 py-3 text-slate-400 text-xs hidden md:table-cell">{sn.description || '—'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(sn)} className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => del(sn)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal.item ? 'Edit Support Need' : 'New Support Need'} onClose={() => setModal(null)}>
          <Field label="Name" required>
            <input className="input-field" placeholder="Support need name"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <textarea className="input-field resize-none" rows={3}
              placeholder="Brief description…"
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary btn-sm">
              {saving ? 'Saving…' : modal.item ? 'Save Changes' : 'Add'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main SmartMatch page ───────────────────────────────────────────────────────
export default function SmartMatch() {
  const [tab, setTab] = useState('qualifications');
  const [data, setData] = useState({ categories: [], qualifications: [] });
  const [supportNeeds, setSupportNeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [main, sn] = await Promise.all([
        api.get('/smart-match'),
        api.get('/smart-match/support-needs'),
      ]);
      setData(main.data);
      setSupportNeeds(sn.data);
    } catch (e) {
      toast.error('Failed to load qualification match data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'qualifications', label: 'Carer Qualifications' },
    { id: 'support-needs',  label: 'Support Needs' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Qualification match"
        description="Define carer qualifications and client support needs — the scheduler ranks staff by how well they overlap."
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'qualifications' && (
        <QualificationsTab
          categories={data.categories}
          qualifications={data.qualifications}
          supportNeeds={supportNeeds}
          onRefresh={load}
        />
      )}
      {tab === 'support-needs' && (
        <SupportNeedsTab supportNeeds={supportNeeds} onRefresh={load} />
      )}
    </div>
  );
}
